import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PageTransition from "../components/PageTransition";
import FaqAccordion from "../components/faq/FaqAccordion";
import CommandCentreShell from "../components/layout/CommandCentreShell";
import { Sparkle } from "../components/alignment-advantage/shared/Sparkle";
import { C } from "../components/alignment-advantage/shared/constants";
import { useAuth } from "../context/AuthContext";
import { useAppNavItems } from "../hooks/useAppNavItems";
import { useFaqItems } from "../hooks/useFaqItems";
import AppNavTourOverlay from "../components/tour/AppNavTourOverlay";
import {
  authBodyTextClass,
  authCardClass,
  authLinkClass,
  authPageShellClass,
  authTitleClass,
} from "../styles/authUi";

/**
 * FAQ page — public when logged out; Command Centre shell when logged in.
 */
const FaqPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { items: appNavItems } = useAppNavItems({ activeKey: "faq" });
  const [tourActive, setTourActive] = useState(false);
  const sidebarOpenRef = useRef<(() => void) | null>(null);

  const faqItems = useFaqItems();

  const ensureNavVisible = useCallback((): void => {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) {
      sidebarOpenRef.current?.();
    }
  }, []);

  const initialOpenId = useMemo(() => {
    const hash = location.hash.replace(/^#/, "");
    if (!hash) {
      return null;
    }
    return faqItems.some((item) => item.id === hash) ? hash : null;
  }, [location.hash, faqItems]);

  const content = (
    <>
      <header className="mb-6 sm:mb-8">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
          <h1 className="text-3xl sm:text-4xl font-bold text-theme-fg shrink-0">FAQ</h1>
          {user && (
            <button
                type="button"
                onClick={() => setTourActive(true)}
                className="shrink min-w-0 sm:ml-auto inline-flex items-center justify-center rounded-xl px-3 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 max-w-full leading-tight text-center"
                style={{
                  background: `linear-gradient(90deg, ${C.coral}, ${C.coralDark})`,
                }}
              >
                Take a quick tour
              </button>
          )}
        </div>
        <p className="mt-2 text-base text-theme-fg-secondary max-w-2xl leading-relaxed">
          {user
            ? "Step-by-step guides for Dashboard, charts, reports, and Settings."
            : "Common questions about sign-in, account setup, and passwords, with the next step for each."}
        </p>
      </header>
      <FaqAccordion items={faqItems} initialOpenId={initialOpenId} />
    </>
  );

  if (!user) {
    // Public FAQ is taller than the auth sign-in card. Do not wrap in
    // auth-page-bg flex centering (overflow:hidden clips) or PageTransition
    // (transform breaks position:fixed). Use a scrollable overlay instead.
    return (
      <div className="fixed inset-0 z-20 overflow-y-auto overscroll-contain">
        <div className={authPageShellClass} aria-hidden="true" />
        <div className="relative z-10 mx-auto w-full max-w-3xl px-4 py-8 sm:py-12 pb-16">
          <div className={`${authCardClass} p-5 sm:p-8`}>
            <div className="space-y-2 mb-6">
              <h2 className={authTitleClass}>FAQ</h2>
              <p className={authBodyTextClass}>
                Login help, account setup, and what to do if you’re stuck.
              </p>
            </div>
            <FaqAccordion items={faqItems} initialOpenId={initialOpenId} />
            <div className="mt-6 text-center text-sm">
              <Link to="/authentication/sign-in" className={authLinkClass}>
                Back to Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const displayName =
    user.user_metadata?.display_name || user.email?.split("@")[0] || "User";
  const userInitial =
    displayName.trim().length > 0
      ? displayName.trim().charAt(0).toUpperCase()
      : "?";

  const sidebarFooter = (
    <div className="px-5 py-4">
      <div className="flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 border border-white/30"
          style={{ background: "rgba(255,255,255,0.15)", color: C.white }}
        >
          {userInitial}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-white truncate">{displayName}</p>
          <p
            className="text-[10px] truncate"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            {user.email ?? ""}
          </p>
        </div>
        <Sparkle size={10} color="rgba(255,255,255,0.60)" />
      </div>
    </div>
  );

  return (
    <PageTransition>
      <CommandCentreShell
        brandLabel="Purple Star Astrology"
        brandSubLabel={displayName}
        contextTitle="FAQ"
        contextSubtitle="Feature how-tos"
        appNavItems={appNavItems}
        sidebarFooter={sidebarFooter}
        onSidebarOpenRef={sidebarOpenRef}
      >
        <div className="relative px-4 sm:px-6 md:px-10 py-6 md:py-10 min-w-0 w-full max-w-4xl">
          {content}
        </div>
      </CommandCentreShell>
      <AppNavTourOverlay
        active={tourActive}
        onClose={() => setTourActive(false)}
        onEnsureNavVisible={ensureNavVisible}
      />
    </PageTransition>
  );
};

export default FaqPage;
